import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, ChevronDown, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import axiosInstance from '../api/axios';
import useAuthStore from '../store/authStore';
import { toast } from 'sonner';

const SupportChatWidget = () => {
  const { user, isAuthenticated } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('list'); // 'list', 'chat', 'new'
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Check maintenance mode
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/maintenance`);
        if (response.ok) {
          const data = await response.json();
          const isAdmin = isAuthenticated && user?.role === 'admin';
          const isAdminPath = window.location.pathname.startsWith('/admin') || window.location.pathname === '/login';
          setIsMaintenanceMode(data.maintenance_mode && !isAdmin && !isAdminPath);
        }
      } catch (error) {
        // Ignore
      }
    };
    checkMaintenance();
  }, [isAuthenticated, user]);
  
  // New conversation form
  const [newForm, setNewForm] = useState({
    subject: '',
    message: '',
    category: 'general',
    email: '',
    name: ''
  });

  const categories = [
    { value: 'general', label: 'Allgemeine Anfrage' },
    { value: 'order', label: 'Bestellungen' },
    { value: 'technical', label: 'Technisches Problem' },
    { value: 'vendor_support', label: 'Verkäufer-Support' }
  ];

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      loadConversations();
    }
  }, [isOpen, isAuthenticated]);

  useEffect(() => {
    scrollToBottom();
  }, [activeConversation?.messages]);

  // Listen for global events from other components (e.g. the DesktopSidebar
  // chat button) so the same widget instance can be opened from anywhere.
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('open-support-chat', handler);
    return () => window.removeEventListener('open-support-chat', handler);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      const response = await axiosInstance.get('/support/conversations/my');
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const loadConversation = async (conversationId) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/support/conversations/${conversationId}`);
      setActiveConversation(response.data);
      setEmailNotifications(response.data.email_notifications_enabled ?? true);
      setView('chat');
    } catch (error) {
      toast.error('Fehler beim Laden der Konversation');
    } finally {
      setLoading(false);
    }
  };

  const handleStartNewConversation = async () => {
    if (!newForm.subject.trim() || !newForm.message.trim()) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    if (!isAuthenticated && !newForm.email) {
      toast.error('Bitte geben Sie Ihre E-Mail-Adresse ein');
      return;
    }

    setSending(true);
    try {
      const payload = {
        subject: newForm.subject,
        message: newForm.message,
        category: newForm.category
      };

      if (!isAuthenticated) {
        payload.email = newForm.email;
        payload.name = newForm.name || 'Gast';
      }

      const response = await axiosInstance.post('/support/conversations/new', payload);
      
      toast.success('Nachricht gesendet!');
      setNewForm({ subject: '', message: '', category: 'general', email: '', name: '' });
      
      if (isAuthenticated) {
        await loadConversations();
        setView('list');
      } else {
        setView('list');
        setIsOpen(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  const handleSendReply = async () => {
    if (!message.trim() || !activeConversation) return;

    setSending(true);
    try {
      await axiosInstance.post(`/support/conversations/${activeConversation.id}/reply`, {
        message: message.trim()
      });
      
      setMessage('');
      await loadConversation(activeConversation.id);
    } catch (error) {
      toast.error('Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  const handleToggleEmailNotifications = async () => {
    if (!activeConversation) return;
    
    try {
      await axiosInstance.put(`/support/conversations/${activeConversation.id}/email-preference`, {
        receive_email_copies: !emailNotifications
      });
      setEmailNotifications(!emailNotifications);
      toast.success(emailNotifications ? 'E-Mail-Benachrichtigungen deaktiviert' : 'E-Mail-Benachrichtigungen aktiviert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('de-DE', { 
      day: '2-digit', 
      month: '2-digit',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-blue-100 text-blue-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'open': return 'Offen';
      case 'pending': return 'Wartet';
      case 'resolved': return 'Gelöst';
      case 'closed': return 'Geschlossen';
      default: return status;
    }
  };

  // Hide during maintenance mode
  if (isMaintenanceMode) {
    return null;
  }

  return (
    <>
      {/* Chat Button - hidden on lg+ (the sidebar provides the desktop entry point) */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed z-50 shadow-md items-center justify-center transition-all
          lg:hidden
          md:bottom-6 md:right-4 md:w-14 md:h-14 md:rounded-full md:bg-[#8B7355] md:hover:bg-[#6D5A47] md:text-white md:hover:scale-110
          bottom-24 right-3 w-9 h-9 border border-[#C4A084] bg-white text-[#8B7355] hover:bg-[#C4A084] hover:text-white flex"
        data-testid="support-chat-button"
      >
        {isOpen ? (
          <X className="h-4 w-4 md:h-6 md:w-6" />
        ) : (
          <MessageCircle className="h-4 w-4 md:h-6 md:w-6" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-40 md:bottom-24 right-3 md:right-4 z-50 w-[calc(100vw-1.5rem)] max-w-md bg-white shadow-2xl border overflow-hidden" data-testid="support-chat-window">
          {/* Header */}
          <div className="bg-[#8B7355] text-white p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold">Kundenservice</h3>
                <p className="text-sm text-white/80">
                  {view === 'list' && 'Wie können wir helfen?'}
                  {view === 'new' && 'Neue Anfrage'}
                  {view === 'chat' && activeConversation?.subject}
                </p>
              </div>
              {view !== 'list' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setView('list');
                    setActiveConversation(null);
                  }}
                  className="text-white hover:bg-white/20"
                >
                  <ChevronDown className="h-4 w-4 mr-1" />
                  Zurück
                </Button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="h-[400px] overflow-y-auto">
            {/* List View */}
            {view === 'list' && (
              <div className="p-4 space-y-4">
                {/* New Conversation Button */}
                <Button
                  onClick={() => setView('new')}
                  className="w-full bg-[#8B7355] hover:bg-[#6D5A47]"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Neue Anfrage starten
                </Button>

                {/* Existing Conversations */}
                {isAuthenticated && conversations.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-600">Ihre Anfragen</h4>
                    {conversations.map((conv) => (
                      <button
                        key={conv.id}
                        onClick={() => loadConversation(conv.id)}
                        className={`w-full text-left p-3 rounded-lg border hover:bg-gray-50 transition-colors ${
                          conv.unread_by_user ? 'bg-blue-50 border-blue-200' : ''
                        }`}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-sm line-clamp-1">{conv.subject}</span>
                          <Badge className={`text-xs ${getStatusColor(conv.status)}`}>
                            {getStatusLabel(conv.status)}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-500">
                          {formatTime(conv.last_message_at)}
                          {conv.unread_by_user && (
                            <span className="ml-2 text-blue-600 font-medium">Neue Antwort</span>
                          )}
                        </p>
                      </button>
                    ))}
                  </div>
                )}

                {/* Info for non-logged in users */}
                {!isAuthenticated && (
                  <div className="text-center text-sm text-gray-500 py-4">
                    <p>Sie können auch ohne Anmeldung eine Anfrage stellen.</p>
                    <p className="mt-1">
                      <a href="/account" className="text-[#8B7355] underline">Anmelden</a> um Ihre Anfragen zu verwalten.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* New Conversation Form */}
            {view === 'new' && (
              <div className="p-4 space-y-4">
                {!isAuthenticated && (
                  <>
                    <div>
                      <Label>Ihr Name</Label>
                      <Input
                        value={newForm.name}
                        onChange={(e) => setNewForm({...newForm, name: e.target.value})}
                        placeholder="Max Mustermann"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>E-Mail *</Label>
                      <Input
                        type="email"
                        value={newForm.email}
                        onChange={(e) => setNewForm({...newForm, email: e.target.value})}
                        placeholder="ihre@email.de"
                        className="mt-1"
                        required
                      />
                    </div>
                  </>
                )}
                
                <div>
                  <Label>Kategorie</Label>
                  <select
                    value={newForm.category}
                    onChange={(e) => setNewForm({...newForm, category: e.target.value})}
                    className="w-full mt-1 border rounded-lg px-3 py-2"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Betreff *</Label>
                  <Input
                    value={newForm.subject}
                    onChange={(e) => setNewForm({...newForm, subject: e.target.value})}
                    placeholder="Worum geht es?"
                    className="mt-1"
                    required
                  />
                </div>

                <div>
                  <Label>Nachricht *</Label>
                  <Textarea
                    value={newForm.message}
                    onChange={(e) => setNewForm({...newForm, message: e.target.value})}
                    placeholder="Beschreiben Sie Ihr Anliegen..."
                    className="mt-1 min-h-[100px]"
                    required
                  />
                </div>

                <Button
                  onClick={handleStartNewConversation}
                  disabled={sending}
                  className="w-full bg-[#8B7355] hover:bg-[#6D5A47]"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Nachricht senden
                </Button>
              </div>
            )}

            {/* Chat View */}
            {view === 'chat' && activeConversation && (
              <div className="flex flex-col h-full">
                {/* Email notification toggle */}
                <div className="p-2 border-b bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <Mail className="h-3 w-3" />
                    <span>E-Mail-Kopien</span>
                  </div>
                  <Switch
                    checked={emailNotifications}
                    onCheckedChange={handleToggleEmailNotifications}
                  />
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                  ) : (
                    activeConversation.messages?.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender === 'admin' ? 'justify-start' : 'justify-end'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                            msg.sender === 'admin'
                              ? 'bg-gray-100 text-gray-800 rounded-bl-none'
                              : 'bg-[#8B7355] text-white rounded-br-none'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                          <p className={`text-xs mt-1 ${msg.sender === 'admin' ? 'text-gray-500' : 'text-white/70'}`}>
                            {formatTime(msg.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply Input */}
                {activeConversation.status !== 'closed' && (
                  <div className="p-3 border-t bg-white">
                    <div className="flex gap-2">
                      <Input
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Ihre Antwort..."
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendReply()}
                        disabled={sending}
                      />
                      <Button
                        onClick={handleSendReply}
                        disabled={sending || !message.trim()}
                        className="bg-[#8B7355] hover:bg-[#6D5A47]"
                      >
                        {sending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SupportChatWidget;
